import type { ReactNode } from 'react';
import { T, Var } from 'gt-react';
import {
  BellIcon,
  FileTextIcon,
  CurrencyDollarIcon,
  PercentIcon,
  ShoppingCartIcon,
  MegaphoneIcon,
  WarningIcon,
  WarningOctagonIcon,
  XCircleIcon,
  SealCheckIcon,
  UserSwitchIcon,
  ChatCircleTextIcon,
  FlagBannerIcon,
  type Icon as PhosphorIcon,
} from '@phosphor-icons/react';
import { NotificationEventType } from '@erp/shared-types';

export type NotificationLevel = 'info' | 'warn' | 'critical';

export interface NotificationRenderConfig {
  /**
   * Icon shown in the avatar circle. May be overridden per-payload by
   * `getIcon` (e.g. SystemAnnouncement escalates to WarningOctagonIcon
   * when payload.level === 'critical').
   */
  Icon: PhosphorIcon;
  /** Optional per-payload icon override; falls back to `Icon` if absent. */
  getIcon?: (payload: Record<string, unknown>) => PhosphorIcon;
  /**
   * Optional severity tint. Drives the avatar's bg/text colors in the
   * row. Return `null` to use the neutral default treatment.
   */
  getLevel?: (payload: Record<string, unknown>) => NotificationLevel | null;
  getTitle: (payload: Record<string, unknown>) => ReactNode;
  getBody: (payload: Record<string, unknown>) => ReactNode;
  /** Returns the deep link to the referenced entity, or null if none. */
  getLink: (
    notifiableType: string | null,
    notifiableId: string | null,
  ) => string | null;
}

const VALID_LEVELS: NotificationLevel[] = ['info', 'warn', 'critical'];

function level(payload: Record<string, unknown>): NotificationLevel | null {
  const v = payload['level'];
  return typeof v === 'string' && (VALID_LEVELS as string[]).includes(v)
    ? (v as NotificationLevel)
    : null;
}

/**
 * Defensive payload accessor — old DB rows may predate payload-shape
 * changes, so renderers fall back to a sensible default rather than
 * throwing if a field is missing.
 */
function str(
  payload: Record<string, unknown>,
  key: string,
  fallback = '',
): string {
  const v = payload[key];
  return typeof v === 'string' ? v : fallback;
}

function num(
  payload: Record<string, unknown>,
  key: string,
  fallback = 0,
): number {
  const v = payload[key];
  return typeof v === 'number' ? v : fallback;
}

export const notificationRenderers: Record<string, NotificationRenderConfig> = {
  [NotificationEventType.SystemAnnouncement]: {
    Icon: MegaphoneIcon,
    // Escalate the icon glyph with severity so the row is recognizable
    // at a glance even before colors are perceived.
    getIcon: (p) => {
      const lvl = level(p);
      if (lvl === 'critical') return WarningOctagonIcon;
      if (lvl === 'warn') return WarningIcon;
      return MegaphoneIcon;
    },
    getLevel: (p) => level(p),
    getTitle: (p) => str(p, 'title', 'Announcement'),
    // Announcement bodies are free-form and may carry intentional newlines /
    // bullet lists. `whitespace-pre-line` honors those line breaks (HTML would
    // otherwise collapse them) while still wrapping long lines. The body is
    // pulled into a const so the dynamic value — not a string literal — is what
    // lands in JSX (keeps i18next/no-literal-string happy).
    getBody: (p) => {
      const body = str(p, 'body');
      return <span className="whitespace-pre-line">{body}</span>;
    },
    getLink: () => null,
  },
  [NotificationEventType.InvoiceOverdue]: {
    Icon: FileTextIcon,
    getTitle: (p) => (
      <T context="notification title for an overdue invoice">
        Invoice <Var>{str(p, 'invoiceNumber')}</Var> is overdue
      </T>
    ),
    getBody: (p) => (
      <T context="notification body for an overdue invoice">
        <Var>{str(p, 'customerName')}</Var> — $
        <Var>{num(p, 'amount').toLocaleString()}</Var>,{' '}
        <Var>{num(p, 'daysOverdue')}</Var> days late
      </T>
    ),
    getLink: (_type, id) => (id ? `/invoices/${id}` : null),
  },
  [NotificationEventType.PaymentReceived]: {
    Icon: CurrencyDollarIcon,
    getTitle: (p) => (
      <T context="notification title for a received payment">
        Payment received: <Var>{str(p, 'paymentNumber')}</Var>
      </T>
    ),
    getBody: (p) => (
      <T context="notification body for a received payment">
        <Var>{str(p, 'customerName')}</Var> — $
        <Var>{num(p, 'amount').toLocaleString()}</Var>
      </T>
    ),
    getLink: (_type, id) => (id ? `/payments/${id}` : null),
  },
  [NotificationEventType.DiscountApprovalNeeded]: {
    Icon: PercentIcon,
    getTitle: () => (
      <T context="notification title for a discount approval request">
        Discount approval needed
      </T>
    ),
    getBody: (p) => (
      <T context="notification body for a discount approval request">
        <Var>{str(p, 'customerName')}</Var> —{' '}
        <Var>{num(p, 'discountPercent')}</Var>% off
      </T>
    ),
    getLink: (_type, id) => (id ? `/discounts/${id}` : null),
  },
  [NotificationEventType.SalesOrderApproved]: {
    Icon: ShoppingCartIcon,
    getTitle: (p) => (
      <T context="notification title for an approved sales order">
        Sales order <Var>{str(p, 'salesOrderNumber')}</Var> approved
      </T>
    ),
    getBody: (p) => str(p, 'customerName'),
    getLink: (_type, id) => (id ? `/sales-orders/${id}` : null),
  },
  [NotificationEventType.ReportReady]: {
    Icon: FileTextIcon,
    getTitle: (p) => (
      <T context="notification title for a generated report">
        Report ready: <Var>{str(p, 'reportName')}</Var>
      </T>
    ),
    getBody: () => null,
    getLink: (_type, id) => (id ? `/reports/${id}` : null),
  },
  [NotificationEventType.WorkflowFailure]: {
    Icon: XCircleIcon,
    getLevel: () => 'critical',
    getTitle: (p) => (
      <T context="notification title for a failed workflow run">
        Workflow failed: <Var>{str(p, 'workflowName', 'workflow')}</Var>
      </T>
    ),
    getBody: (p) => str(p, 'errorMessage'),
    getLink: (_type, _id) => {
      // Deep-link to the run itself, not the notifiable wrapper, so the
      // user lands on the failing run timeline directly.
      return null;
    },
  },
  [NotificationEventType.WorkflowApprovalRequested]: {
    Icon: SealCheckIcon,
    getTitle: (p) => (
      <T context="notification title for a workflow approval request">
        Approval requested: <Var>{str(p, 'workflowName', 'workflow')}</Var>
      </T>
    ),
    getBody: (p) =>
      str(p, 'instructions') || (
        <T context="notification body for a workflow approval request with no instructions">
          A workflow step is waiting for your approval.
        </T>
      ),
    getLink: () => null,
  },
  [NotificationEventType.TaskReassigned]: {
    Icon: UserSwitchIcon,
    getTitle: (p) => (
      <T context="notification title for a task reassigned to the recipient">
        Task assigned to you: <Var>{str(p, 'taskTitle', 'task')}</Var>
      </T>
    ),
    getBody: (p) =>
      str(p, 'reason') || (
        <T context="notification body for a task reassigned with no reason given">
          A task has been reassigned to you.
        </T>
      ),
    getLink: (_type, id) => (id ? `/inbox?tab=tasks&taskId=${id}` : null),
  },
  [NotificationEventType.CollectionThreadAssigned]: {
    Icon: UserSwitchIcon,
    getTitle: (p) => (
      <T context="notification title for a collection thread assigned to the recipient">
        Thread assigned to you: <Var>{str(p, 'threadTitle', 'thread')}</Var>
      </T>
    ),
    getBody: (p) =>
      str(p, 'customerName') || (
        <T context="notification body for a collection thread assignment">
          A collection thread was assigned to you.
        </T>
      ),
    getLink: (_type, id) => collectionThreadLink(id),
  },
  [NotificationEventType.CollectionThreadCommented]: {
    Icon: ChatCircleTextIcon,
    getTitle: (p) => (
      <T context="notification title for a new comment on a collection thread">
        New comment on <Var>{str(p, 'threadTitle', 'a thread')}</Var>
      </T>
    ),
    getBody: (p) =>
      str(p, 'commentPreview') || (
        <T context="notification body for a new collection thread comment">
          A comment was added to a thread you follow.
        </T>
      ),
    getLink: (_type, id) => collectionThreadLink(id),
  },
  [NotificationEventType.CollectionThreadStatusChanged]: {
    Icon: SealCheckIcon,
    getTitle: (p) =>
      str(p, 'status') === 'resolved' ? (
        <T context="notification title for a resolved collection thread">
          Thread resolved: <Var>{str(p, 'threadTitle', 'thread')}</Var>
        </T>
      ) : (
        <T context="notification title for a reopened collection thread">
          Thread reopened: <Var>{str(p, 'threadTitle', 'thread')}</Var>
        </T>
      ),
    getBody: (p) =>
      str(p, 'customerName') || (
        <T context="notification body for a collection thread status change">
          The status of a thread you follow changed.
        </T>
      ),
    getLink: (_type, id) => collectionThreadLink(id),
  },
  [NotificationEventType.CollectionThreadOpened]: {
    Icon: FlagBannerIcon,
    getTitle: (p) => (
      <T context="notification title for a newly opened collection thread">
        New collection thread: <Var>{str(p, 'threadTitle', 'thread')}</Var>
      </T>
    ),
    getBody: (p) =>
      str(p, 'customerName') || (
        <T context="notification body for a newly opened collection thread">
          A new collection thread was opened.
        </T>
      ),
    getLink: (_type, id) => collectionThreadLink(id),
  },
};

/** Deep link that opens a collection thread in the Collections list view. */
function collectionThreadLink(id: string | null): string | null {
  return id
    ? `/accounts-receivable/collections?tab=threads&view=list&threadId=${id}`
    : null;
}

/** Fallback used when an event_type has no registered renderer. */
const fallbackRenderer: NotificationRenderConfig = {
  Icon: BellIcon,
  getTitle: () => (
    <T context="fallback notification title for an unknown event type">
      Notification
    </T>
  ),
  getBody: () => null,
  getLink: () => null,
};

export function getNotificationRenderer(
  eventType: string,
): NotificationRenderConfig {
  return notificationRenderers[eventType] ?? fallbackRenderer;
}
